import React, { useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Collapse,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import clsx from 'clsx';
import { format, parseJSON } from 'date-fns';
import { makeStyles } from 'makeStyles';

import { DATE_FORMAT } from 'common/constants';

import { DetailsButton } from 'commonComponents/DetailsButton';

import type { CustomerDetails } from 'api/customers';
import { LicenseListEntry, useCustomerLicenses } from 'api/licenses';
import { useCurrentProduct } from 'api/products';
import { ExpandLessIcon, ExpandMoreIcon } from 'icons';
import { NewBadge } from 'icons/NewBadge';
import { expirationDate } from 'licenses/utils';

import { CreateIcon } from '../icons/CreateIcon';
import { ROUTES } from '../routes/routes';

const useStyles = makeStyles()(theme => ({
    cardContent: {
        paddingTop: 0,
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    rowError: { color: theme.palette.error.main },
    rowInvalidated: { textDecoration: 'line-through' },
}));

function getNewButtonStatus(
    customer: CustomerDetails,
    currentProduct: { id: number; name: string }
): { enabled: boolean; title: string } {
    if (!customer || !currentProduct) {
        return { enabled: false, title: '' };
    }

    if (customer.invalidated) {
        return { enabled: false, title: 'This customer is invalidated.' };
    }

    const customerProducts = customer.products.map(product => product.id);
    const matchingProductSelected = customerProducts.includes(currentProduct.id);
    if (matchingProductSelected) {
        return { enabled: true, title: `Create new ${currentProduct.name} license` };
    }
    return {
        enabled: false,
        title: `You cannot create a new license for this customer because the product is not assigned to them.
        
Select one of the following products from the top right:

${customer.products.map(product => product.name).join('\n\n')}`,
    };
}

export function CustomerLicenses({
    customer,
    highlightLicense,
}: {
    customer: CustomerDetails;
    highlightLicense: number | undefined;
}) {
    const navigate = useNavigate();
    const { data: currentProduct } = useCurrentProduct();

    const { classes } = useStyles();
    const [open, setOpen] = useState(!!highlightLicense);

    const { data, error } = useCustomerLicenses({ customerId: customer.id });
    const { enabled: enableNewLicenseButton, title } = useMemo(
        () => getNewButtonStatus(customer, currentProduct),
        [customer, currentProduct]
    );

    if (error || !data || !currentProduct) {
        return null;
    }

    const handleNewButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation(); // Prevent toggling expand/collapse
        if (customer == null) return;
        const route = generatePath(ROUTES.LICENSE_CREATE_WITH_CUSTOMER, { customerId: customer.id.toString() });

        navigate(route);
    };

    return (
        <Card>
            <CardHeader
                onClick={() => setOpen(!open)}
                subheader="Licenses"
                action={
                    <>
                        <span title={title}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CreateIcon />}
                                onClick={handleNewButtonClick}
                                style={{ marginRight: '8px' }}
                                disabled={!enableNewLicenseButton}
                            >
                                New
                            </Button>
                        </span>
                        <IconButton size="large" aria-label={open ? 'Collapse' : 'Expand'}>
                            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </>
                }
                data-testid="CustomerLicenses_CardHeader"
            />
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Divider />
                <CardContent className={classes.cardContent} data-testid="CustomerLicenses_CardContent">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ padding: 0 }}></TableCell>
                                <TableCell sx={{ paddingLeft: '3px' }}>Creation Date</TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell>Configuration</TableCell>
                                <TableCell>S/N</TableCell>
                                <TableCell>Sales Order</TableCell>
                                <TableCell>Activations</TableCell>
                                <TableCell>Expiration Date</TableCell>
                                <TableCell sx={{ width: 30 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.results.map(license => {
                                return (
                                    <CustomerLicenseEntry
                                        key={license.id}
                                        license={license}
                                        highlight={license.id === highlightLicense}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Collapse>
        </Card>
    );
}

function CustomerLicenseEntry({ license, highlight }: { license: LicenseListEntry; highlight: boolean }) {
    const { classes } = useStyles();
    const cellClasses = clsx(license.invalidated && classes.rowInvalidated, license.isExpired && classes.rowError);
    const cellStyles = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 100,
    };

    return (
        <TableRow>
            <TableCell sx={{ padding: 0, width: 24 }}>{highlight && <NewBadge sx={{ paddingTop: '3px' }} />}</TableCell>
            <TableCell sx={{ paddingLeft: '3px', ...cellStyles }} className={cellClasses}>
                {license.creation_date && format(parseJSON(license.creation_date), DATE_FORMAT)}
            </TableCell>
            <TableCell sx={cellStyles} className={cellClasses}>
                {license.id}
            </TableCell>
            <TableCell sx={cellStyles} className={cellClasses}>
                {license.configuration}
            </TableCell>
            <TableCell sx={cellStyles} className={cellClasses}>
                {license.serial_number}
            </TableCell>
            <TableCell sx={cellStyles} className={cellClasses}>
                {license.sales_order_number}
            </TableCell>
            <TableCell sx={cellStyles} className={cellClasses}>
                {license.licenseFilesCount}
            </TableCell>
            <TableCell sx={cellStyles} className={cellClasses}>
                {expirationDate(license, license.expiration_date)}
            </TableCell>
            <TableCell sx={{ width: 30 }}>
                <DetailsButton route={generatePath(ROUTES.LICENSE_DETAIL, { id: license.id.toString() })} />
            </TableCell>
        </TableRow>
    );
}
